import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { SerializedError } from '@reduxjs/toolkit';

/** RFC 7807 body, as produced by the backend's GlobalExceptionHandler. */
export interface ProblemDetail {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  /** Added for validation failures: field name -> message. */
  errors?: Record<string, string>;
}

export type ApiError = FetchBaseQueryError | SerializedError | undefined;

function isFetchError(error: ApiError): error is FetchBaseQueryError {
  return !!error && 'status' in error;
}

export function problemOf(error: ApiError): ProblemDetail | undefined {
  if (isFetchError(error) && typeof error.data === 'object' && error.data !== null) {
    return error.data as ProblemDetail;
  }
  return undefined;
}

export function statusOf(error: ApiError): number | undefined {
  if (!isFetchError(error)) return undefined;
  return typeof error.status === 'number' ? error.status : undefined;
}

/** Per-field validation messages, for wiring straight into form inputs. */
export function fieldErrorsOf(error: ApiError): Record<string, string> {
  return problemOf(error)?.errors ?? {};
}

/**
 * One human-readable line for any failure. Prefers the server's `detail`, then its `title`, and
 * only falls back to a generic message for transport-level errors that carry no body.
 */
export function messageOf(error: ApiError, fallback = 'Something went wrong. Please try again.'): string {
  const problem = problemOf(error);
  if (problem?.detail) return problem.detail;
  if (problem?.title) return problem.title;

  if (isFetchError(error)) {
    if (error.status === 'FETCH_ERROR') {
      return "Couldn't reach the server. Check that the API is running.";
    }
    if (error.status === 401) return 'You need to be signed in to do that.';
    if (error.status === 403) return "You don't have permission to do that.";
    if (error.status === 404) return "That doesn't exist (or isn't public).";
  }
  if (error && 'message' in error && error.message) return error.message;
  return fallback;
}
